/**
 * src/schema.ts — Zod schemas are the SINGLE SOURCE OF TRUTH for every data shape
 * (§8.2). The TypeScript interfaces in `types.ts` are derived from these via `z.infer`,
 * so runtime validation and compile-time types can never drift.
 *
 * Validation boundaries (all use `safeParse`, never throw into the UI):
 *   1. External JSON (sofr.json + its bundled fallback)
 *   2. IndexedDB reads (after the migration chain)
 *   3. ScenarioInputs before each projection (coerce + clamp)
 *
 * Range-violations are CLAMPED (not rejected) via transforms; genuinely malformed
 * data (non-finite numbers, missing required cross-field values) produces issues.
 */
import { z } from 'zod';

/** Data-shape version stamped on every persisted Scenario (§9). Distinct from the
 *  IndexedDB structural version. Bumped whenever the ScenarioInputs shape evolves. */
export const CURRENT_SCHEMA_VERSION = 5;

// ───────────────────────────── numeric helpers ─────────────────────────────

/** Clamp a number into [min, max]. Pure; exported for direct unit testing. */
export function clampNumber(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

/** Coerced finite number (rejects NaN/Infinity — the realistic failure mode). */
const finiteNumber = () =>
  z.coerce.number().refine((n) => Number.isFinite(n), { message: 'Must be a finite number' });

/** Coerced finite number clamped into [min, max] rather than rejected (§8.2). */
const clamped = (min: number, max: number) =>
  finiteNumber().transform((n) => clampNumber(n, min, max));

/** Coerced finite, clamped to ≥ 0 (amounts that cannot be negative). */
const nonNegative = () => finiteNumber().transform((n) => (n < 0 ? 0 : n));

/** Coerced finite integer clamped into [min, max]. */
const clampedInt = (min: number, max: number) =>
  finiteNumber().transform((n) => clampNumber(Math.round(n), min, max));

/** Non-negative integer (e.g. month indices). */
const nonNegativeInt = () => finiteNumber().transform((n) => Math.max(0, Math.round(n)));

/** ISO calendar date string `YYYY-MM-DD` that parses to a real date. */
export const isoDateSchema = z
  .string()
  .refine((s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s)), {
    message: 'Must be an ISO date (YYYY-MM-DD)',
  });

// ───────────────────────────── enums ─────────────────────────────

export const paymentModeSchema = z.enum(['interest_only', 'fixed', 'capitalize', 'custom']);
export const ratePathTypeSchema = z.enum(['flat', 'linear', 'custom', 'stress']);
export const stressPresetSchema = z.enum([
  'rising_300_24m',
  'falling_200_24m',
  'spike_500_revert',
  'hike_cycle_2022',
  'crash_2008',
  'equity_bear',
  'rate_spike_assets',
]);
export const drawFrequencySchema = z.enum(['monthly', 'quarterly', 'annual', 'one_time']);
export const dividendHandlingSchema = z.enum(['reinvest', 'service_interest', 'spend']);
export const assetClassSchema = z.enum([
  'cash_mmf',
  'treasury',
  'broad_etf_large_cap',
  'equity',
  'mutual_fund',
  'fixed_income',
  'illiquid_restricted',
  'other',
]);
export const liquidationPolicySchema = z.enum([
  'schwab_liquidity',
  'most_tax_efficient',
  'lowest_advance_first',
  'preserve_high_advance',
  'pro_rata',
]);
export const filingStatusSchema = z.enum(['single', 'mfj', 'mfs', 'hoh']);
export const themeSchema = z.enum(['light', 'dark', 'system']);
export const complexityModeSchema = z.enum(['simple', 'advanced']);

// ───────────────────────────── data sources (§7) ─────────────────────────────

export const sofrObservationSchema = z.object({
  date: isoDateSchema,
  rate: finiteNumber(),
});

// ───────── capital-gains bracket table (§6.12b, §7.3) ─────────

/** One filing status's long-term-gains thresholds. Hand-maintained from an annual IRS
 *  Revenue Procedure; the table carries the year it came from so a gate can catch drift. */
export const capGainsBracketRowSchema = z.object({
  label: z.string(),
  maxZeroRateAmount: nonNegative(),
  maxFifteenRateAmount: nonNegative(),
  niitThreshold: nonNegative(),
});

export const capGainsBracketTableSchema = z.object({
  /** The tax year these thresholds are for. Drives the staleness gate (§7.3). */
  taxYear: z.number().int(),
  source: z.string(),
  sourceLabel: z.string(),
  verifiedOn: isoDateSchema,
  niitRatePct: clamped(0, 100),
  statuses: z.record(filingStatusSchema, capGainsBracketRowSchema),
});

// ───────────────────────────── tax fields (§6.12, §6.20) ─────────────────────────────

export const taxFieldsSchema = z.object({
  longTermCapGainsRatePct: clamped(0, 100).default(15),
  stateCapGainsRatePct: clamped(0, 100).default(0),
  ordinaryIncomeRatePct: clamped(0, 100).default(32),
  interestDeductible: z.boolean().default(false),
  estateTaxEnabled: z.boolean().default(false),
  estateExemption: nonNegative().default(0),
  estateTaxRatePct: clamped(0, 100).default(0),

  // Bracketed capital gains (§6.12b). Off by default: brackets are meaningless without
  // the income fields below, and defaulting them on with zero income would report
  // near-zero tax — a worse answer than the flat rate, handed to someone who never
  // opened this section.
  useBracketedCapGains: z.boolean().default(false),
  filingStatus: filingStatusSchema.default('single'),
  /** Taxable income EXCLUDING these gains — sets where the gain stacks in the brackets. */
  otherTaxableIncome: nonNegative().default(0),
  /** MAGI excluding these gains — drives the NIIT threshold test (differs from taxable income). */
  otherMagi: nonNegative().default(0),
  /** Grow the federal thresholds with inflation, as the IRS does annually (§6.12b). */
  indexBracketsToInflation: z.boolean().default(true),
  /** Per-threshold overrides; null means "use the stamped table for this filing status". */
  bracketZeroMaxOverride: nonNegative().nullable().default(null),
  bracketFifteenMaxOverride: nonNegative().nullable().default(null),
  niitThresholdOverride: nonNegative().nullable().default(null),
});

/** The reusable tax profile saved once in appState; defaults for NEW scenarios (§6.20). */
export const taxProfileSchema = taxFieldsSchema;

// ───────────────────────────── rate path (§6.5) ─────────────────────────────

export const ratePathSchema = z.object({
  type: ratePathTypeSchema.default('flat'),
  /** The benchmark the loan floats on. SOFR only PRE-FILLS it (§7.1) — a line priced off
   *  Prime or a house base rate is modeled by editing it, so the field is not SOFR-named. */
  baseRatePct: finiteNumber().default(0),
  annualDeltaBps: finiteNumber().optional(),
  anchors: z
    .array(z.object({ monthIndex: nonNegativeInt(), baseRate: finiteNumber() }))
    .optional(),
  stressPreset: stressPresetSchema.optional(),
  floorPct: finiteNumber().nullable().optional(),
  capPct: finiteNumber().nullable().optional(),
});

export const scheduledAmountSchema = z.object({
  monthIndex: nonNegativeInt(),
  amount: finiteNumber(),
});

const collateralShockSchema = z.object({
  monthIndex: nonNegativeInt(),
  pctChange: finiteNumber(),
});

// ───────────────────────────── collateral buckets (§6.21) ─────────────────────────────

export const collateralBucketSchema = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  label: z.string().default(''),
  assetClass: assetClassSchema,
  value: nonNegative().default(0),
  advanceRate: clamped(0, 1).default(0),
  eligible: z.boolean().default(true),
  expectedReturnAnnualPct: finiteNumber().default(0),
  volatilityPct: nonNegative().default(0),
  dividendYieldAnnualPct: nonNegative().default(0),
  costBasis: nonNegative().default(0),
});

// ───────────────────────────── Monte Carlo (§6.18) ─────────────────────────────

export const monteCarloConfigSchema = z.object({
  runs: clampedInt(1, 10000).default(1000),
  seed: clampedInt(0, Number.MAX_SAFE_INTEGER).default(12345),
  volatilityPct: nonNegative().default(15),
  lognormal: z.boolean().default(false),
  stochasticBaseRate: z.boolean().default(false),
  crossAssetCorrelation: clamped(0, 1).default(0.3),
});

// ───────────────────────────── ScenarioInputs (§8) ─────────────────────────────

export const scenarioInputsSchema = z
  .object({
    // Portfolio & collateral (single-portfolio fields; used when useBuckets === false)
    portfolioValue: nonNegative().default(1_000_000),
    blendedAdvanceRate: clamped(0, 1).default(0.7),
    loanValueOverride: nonNegative().nullable().default(null),
    /**
     * The lender's minimum loan value of collateral (§6.7). A call triggers if LVC falls
     * below it, independent of the loan balance. Schwab PAL and Fidelity SBLOC both set
     * this at $100,000; enter 0 for a line with no such floor.
     */
    minimumLoanValue: nonNegative().default(100_000),
    portfolioExpectedReturnAnnualPct: finiteNumber().default(7),
    dividendYieldAnnualPct: nonNegative().default(0),
    dividendHandling: dividendHandlingSchema.default('reinvest'),
    contributions: z.array(scheduledAmountSchema).default([]),
    collateralShocks: z.array(collateralShockSchema).default([]),

    // Collateral buckets (optional, Advanced)
    useBuckets: z.boolean().default(false),
    collateralBuckets: z.array(collateralBucketSchema).default([]),
    liquidationPolicy: liquidationPolicySchema.default('schwab_liquidity'),

    // Rate: APR(t) = baseRate(t) + spreadPct (§6.1).
    /**
     * The lender's spread over the base rate, in percentage points. Whatever the lender
     * quotes you — after any relationship discount — goes here directly. Providers set it
     * from line size or loan value at origination; this app takes the resulting number.
     */
    spreadPct: finiteNumber().default(2.9),
    dayCountBasis: z
      .union([z.literal(360), z.literal(365)])
      .default(360),
    ratePath: ratePathSchema.default({ type: 'flat', baseRatePct: 0 }),

    // Borrowing
    initialBalance: nonNegative().default(0),
    drawFrequency: drawFrequencySchema.default('monthly'),
    drawAmount: nonNegative().default(5_000),
    drawStartMonth: nonNegativeInt().default(0),
    drawDurationMonths: nonNegativeInt().nullable().default(null),
    lumpDraws: z.array(scheduledAmountSchema).default([]),

    // Repayment
    paymentMode: paymentModeSchema.default('interest_only'),
    fixedPayment: nonNegative().nullable().default(null),
    customPayments: z.array(scheduledAmountSchema).default([]),
    extraPayments: z.array(scheduledAmountSchema).default([]),

    // Deployed proceeds (borrow-to-invest)
    proceedsDeployed: z.boolean().default(false),
    proceedsReturnAnnualPct: finiteNumber().default(0),

    // Taxes & estate
    costBasis: nonNegative().nullable().default(null),
    costBasisPct: clamped(0, 100).default(50),
    tax: taxFieldsSchema.default({}),
    modelForcedLiquidation: z.boolean().default(true),
    cureTargetLtv: clamped(0, 1).default(0.9),

    // Projection & display
    years: clampedInt(1, 40).default(10),
    inflationAnnualPct: finiteNumber().default(2.5),
  })
  .superRefine((inputs, ctx) => {
    // Cross-field rules (§8.2 boundary 3).
    if (inputs.paymentMode === 'fixed' && inputs.fixedPayment === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fixedPayment'],
        message: 'A fixed payment amount is required when payment mode is "fixed".',
      });
    }
    if (inputs.paymentMode === 'custom' && inputs.customPayments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customPayments'],
        message: 'At least one custom payment is required when payment mode is "custom".',
      });
    }
    if (inputs.useBuckets && inputs.collateralBuckets.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['collateralBuckets'],
        message: 'Add at least one collateral bucket when "Split into asset buckets" is on.',
      });
    }
  });

// ───────────────────────────── Scenario + persisted app state (§8, §9) ─────────────────────────────

export const scenarioSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  schemaVersion: z.number().default(CURRENT_SCHEMA_VERSION),
  inputs: scenarioInputsSchema,
});

export const uiStateSchema = z.object({
  theme: themeSchema.default('system'),
  complexityMode: complexityModeSchema.default('simple'),
  onboardingComplete: z.boolean().default(false),
  lastScenarioId: z.string().nullable().default(null),
  comparisonSelection: z.array(z.string()).max(3).default([]),
  showRealDollars: z.boolean().default(false),
});
